import React, {useCallback, useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useAuth} from "../../providers/AuthenticationProvider";
import {
    Chart,
    ChartCategoryAxis,
    ChartCategoryAxisItem,
    ChartLegend,
    ChartSeries,
    ChartSeriesItem,
    ChartSeriesItemTooltip,
    ChartTitle,
    ChartValueAxis,
    ChartValueAxisItem
} from "@progress/kendo-react-charts";
import {pathianDataApiService} from "../../services/PathianDataApiService";
import {Dimmer, Dropdown, Form, Loader} from "semantic-ui-react";
import {SemanticToastContainer, toast} from "react-semantic-toasts";
import {useCustomers} from "../../providers/CustomerProvider";
import styles from "../DataInspector/DataInspector.module.css";
import {customerService} from "../../services/CustomerService";
import {useDataInspector} from "../../providers/DataInspectorProvider";

const toastSection = {
    marginTop: '2rem',
    whiteSpace: 'pre',
    width: '50%',
};

const WEB_PAGE = 'Group Report';

export const GroupReport = () => {
    const {checkAuth, getUserType} = useAuth();
    const {customersList, getAllCustomers, selectedCustomer, setSelectedCustomer} = useCustomers();
    const {
        getDataInspectorBuildings,
        getDataInspectorEquipment,
        buildings,
        equipment
    } = useDataInspector();

    const today = new Date();

    const [loading, setLoading] = useState(false);
    const [aggregationField, setAggregationField] = useState('oat');
    const [startDate, setStartDate] = useState(`${today.getFullYear()}-01-01`);
    const [endDate, setEndDate] = useState(today.toISOString().split('T')[0]);
    const [availablePointCodes, setAvailablePointCodes] = useState([]);
    const [availableFunctionNames, setAvailableFunctionNames] = useState([]);
    const [selectedPointCodes, setSelectedPointCodes] = useState([]);
    const [selectedFunctionNames, setSelectedFunctionNames] = useState([]);
    const [groupReportData, setGroupReportData] = useState(null);
    const [buildingOptions, setBuildingOptions] = useState([]);
    const [equipmentOptions, setEquipmentOptions] = useState([]);
    const [buildingSelected, setBuildingsSelected] = useState();
    const [equipmentSelected, setEquipmentSelected] = useState([]);
    const [allPoints, setAllPoints] = useState(true);
    const [allFunctions, setAllFunctions] = useState(true);

    const navigate = useNavigate();

    const aggregationTypes = [
        {key: "oat", text: "Outdoor Air Temperature", value: "oat"},
        {key: "dewpt", text: "Dew Point", value: "dewpt"},
        {key: "enthalpy", text: "Enthalpy", value: "enthalpy"}
    ]

    checkAuth(getUserType(), WEB_PAGE) && navigate('/');

    useEffect(() => {
        getAllCustomers();
    }, [getAllCustomers]);

    const fetchPointsAndFunctions = useCallback(async () => {
        let selectedEquipmentIds = [];
        if (equipmentSelected && equipmentSelected.length > 0) {
            selectedEquipmentIds = equipmentSelected;
        } else if (buildings && buildings.length > 0) {
            selectedEquipmentIds = buildingSelected;
        } else {
            selectedEquipmentIds = [];
        }
        const pointsResponse = await customerService.getDistinctPoints(selectedCustomer, selectedEquipmentIds, allPoints);
        setAvailablePointCodes(pointsResponse);
        const functionsResponse = await customerService.getDistinctFunctions(selectedCustomer, selectedEquipmentIds, allFunctions);
        setAvailableFunctionNames(functionsResponse);
    }, [selectedCustomer, equipmentSelected, buildingSelected, allPoints, allFunctions]);

    useEffect(() => {
        if (selectedCustomer) {
            fetchPointsAndFunctions();
        }
    }, [selectedCustomer, buildingSelected, equipmentSelected, fetchPointsAndFunctions, allPoints, allFunctions]);

    const normalizeData = (data) => {
        Object.keys(data.pointsOrFunctions).forEach(pointOrFunction => {
            const allKeys = new Set(data.pointsOrFunctions[pointOrFunction].flatMap(x => x.data.map(y => y.key)));
            const sortedKeys = Array.from(allKeys).sort((a, b) => a - b);
            data.pointsOrFunctions[pointOrFunction].forEach(equipment => {
                const keyMap = new Map(equipment.data.map(x => [x.key, x]));
                equipment.data = sortedKeys.map(key => ({
                    key,
                    averageValue: keyMap.get(key)?.averageValue || null
                }));
            });
        });

        return data;
    }

    const handleSubmit = async () => {
        setLoading(true);
        const groupId = customersList.find(x => x.id === selectedCustomer).groupId;
        if (groupId) {
            const formData = {
                groupId,
                aggregationField,
                startDate,
                endDate: `${endDate}T23:59:59`,
                buildingIds: buildingSelected,
                equipmentIds: equipmentSelected,
                points: selectedPointCodes,
                functions: selectedFunctionNames
            }
            const response = await pathianDataApiService.getGroupReportData(formData);
            const normalizedData = normalizeData(response);
            setGroupReportData(normalizedData);
        }
        else {
            toast({
                type: 'error',
                title: 'Error',
                description: 'Customer has no group data',
                time: 5000
            });
        }
        setLoading(false);
    }

    const getXAxisTitle = useCallback(() => {
        if (aggregationField === 'oat') {
            return 'Outdoor Air Temperature (°F)';
        }
        else if (aggregationField === 'dewpt') {
            return 'Dew Point (°F)';
        }
        else if (aggregationField === 'enthalpy') {
            return 'Enthalpy (J)';
        }
        else {
            return aggregationField;
        }
    }, [aggregationField]);

    const getYAxisTitle = (key) => {
        if (key in availablePointCodes) {
            return `${availablePointCodes[key].description} ${availablePointCodes[key].unit ? `(${availablePointCodes[key].unit})` : ''}`;
        }
        else if (key in availableFunctionNames) {
            return `${availableFunctionNames[key].description} ${availableFunctionNames[key].unit ? `(${availableFunctionNames[key].unit})` : ''}`;
        }
        else {
            return key;
        }
    }

    const tooltipRender = ({point}) => {
        return (
            <div>
                <div>{point.series.name}</div>
                <div>X: {point.category}</div>
                <div>Y: {point.value}</div>
            </div>
        );
    }

    useEffect(() => {
        if (selectedCustomer) {
            const groupId = customersList.find(x => x.id === selectedCustomer).groupId;
            getDataInspectorBuildings(groupId, false);
        }

        setBuildingsSelected([]);
        setEquipmentSelected([]);
    }, [selectedCustomer, customersList]);

    useEffect(() => {
        let tempOptions = [];
        setBuildingOptions([]);
        buildings.forEach(equipmentItem => {
            tempOptions.push({
                key: equipmentItem.id,
                value: equipmentItem.id,
                text: `(${equipmentItem.id}) ${equipmentItem.name}`,
            });
        });
        setBuildingOptions(tempOptions);
    }, [buildings]);

    useEffect(() => {
        let tempOptions = [];
        setEquipmentOptions([]);
        equipment.forEach(equipmentItem => {
            tempOptions.push({
                key: equipmentItem.id,
                value: equipmentItem.id,
                text: `(${equipmentItem.id}) ${equipmentItem.name}`,
            });
        });
        setEquipmentOptions(tempOptions);
    }, [equipment]);

    const handleBuildingChange = buildingIds => {
        setBuildingsSelected(buildingIds);

        if(buildingIds.length > 0) {
            getDataInspectorEquipment({
                buildingIds: buildingIds ?? [],
                showStale: false
            });
        }

        setEquipmentSelected([]);
        setEquipmentOptions([]);
    };

    const handleEquipmentChange = equipmentIds => {
        setEquipmentSelected(equipmentIds);
    };

    return (
        <div>
            <div className='pageHeader'>
                <h1>Group Report</h1>
            </div>
            <Dimmer active={loading}>
                <Loader size="massive" active={loading}></Loader>
            </Dimmer>

            <Form style={{ maxWidth: '900px' }}>
                <Form.Group widths='equal'>
                    <Form.Field
                        label={'Customer'}
                        control={Dropdown}
                        fluid
                        selection
                        options={customersList.map(x => {
                            const text =
                                x?.firstname && x?.lastname
                                    ? `${x?.companyName} (${x?.firstname} ${x?.lastname})`
                                    : x?.companyName;
                            return {
                                key: x?.id,
                                text: text,
                                value: x?.id,
                            };
                        })}
                        value={selectedCustomer}
                        style={{height: '38px'}}
                        onChange={(e, selected) => {
                            const customer = customersList.find(x => x.id === selected.value);
                            setSelectedCustomer(customer.id);
                        }}
                        search
                    />
                    <Form.Field
                        label={'Aggregation Type'}
                        control={Dropdown}
                        fluid
                        selection
                        options={aggregationTypes}
                        value={aggregationField}
                        style={{height: '38px'}}
                        onChange={(e, selected) => setAggregationField(selected.value)}
                        search
                    />
                </Form.Group>
                <Form.Group widths='equal'>
                    <Form.Input
                        className={styles.inspectorInput}
                        type={'date'}
                        defaultValue={startDate}
                        label="Time Range (start date)"
                        placeholder="Time Range"
                        onChange={e => setStartDate(e.target.value)}
                    />
                    <Form.Input
                        className={styles.inspectorInput}
                        type={'date'}
                        defaultValue={endDate}
                        label="Time Range (end date)"
                        placeholder="Time Range"
                        onChange={e => setEndDate(e.target.value)}
                    />
                </Form.Group>
                <Form.Group widths='equal'>
                    <Form.Field
                        control={Dropdown}
                        style={{minHeight: '38px', height: 'auto'}}
                        label="Buildings"
                        placeholder="Buildings"
                        options={buildingOptions}
                        value={buildingSelected}
                        onChange={(e, {value}) => handleBuildingChange(value)}
                        multiple
                        selection
                        search
                        clearable
                    />
                    <Form.Field
                        control={Dropdown}
                        style={{minHeight: '38px', height: 'auto'}}
                        label="Equipment"
                        placeholder="Equipment"
                        options={equipmentOptions}
                        value={equipmentSelected}
                        onChange={(e, {value}) => handleEquipmentChange(value)}
                        multiple
                        selection
                        search
                        clearable
                    />
                </Form.Group>
                <Form.Group widths='equal'>
                    <Form.Field style={{marginTop: '24px', display: 'flex', alignItems: 'center', gap: 8}}>
                        <label style={{margin: 0}}>Filtered Points</label>
                        <Form.Checkbox
                            toggle
                            checked={allPoints}
                            onChange={(e, data) => setAllPoints(!!data.checked)}
                        />
                        <label style={{margin: 0}}>All Points</label>
                    </Form.Field>
                    <Form.Field style={{marginTop: '24px', display: 'flex', alignItems: 'center', gap: 8}}>
                        <label style={{margin: 0}}>Filtered Functions</label>
                        <Form.Checkbox
                            toggle
                            checked={allFunctions}
                            onChange={(e, data) => setAllFunctions(!!data.checked)}
                        />
                        <label style={{margin: 0}}>All Functions</label>
                    </Form.Field>
                </Form.Group>
                <Form.Group widths='equal'>
                    <Form.Field
                        label={'Points'}
                        control={Dropdown}
                        fluid
                        selection
                        options={Object.values(availablePointCodes).map(x => ({
                            key: x.pointCode,
                            text: `${x.description} (${x.pointCode})`,
                            value: x.pointCode
                        }))}
                        value={selectedPointCodes}
                        style={{minHeight: '38px'}}
                        onChange={(e, {value}) => {
                            setSelectedPointCodes(value);
                        }}
                        search
                        multiple
                        clearable
                    />
                    <Form.Field
                        label={'Functions'}
                        control={Dropdown}
                        fluid
                        selection
                        options={Object.values(availableFunctionNames).map(x => ({
                            key: x.name,
                            text: `${x.description} (${x.name})`,
                            value: x.name
                        }))}
                        value={selectedFunctionNames}
                        style={{minHeight: '38px'}}
                        onChange={(e, {value}) => {
                            setSelectedFunctionNames(value);
                        }}
                        search
                        multiple
                        clearable
                    />
                </Form.Group>
                <Form.Group>
                    <Form.Button
                        style={{marginTop: '24px', height: '38px'}}
                        size={'large'}
                        type={'button'}
                        color={'blue'}
                        onClick={() => handleSubmit()}
                    >
                        Generate Report
                    </Form.Button>
                </Form.Group>
            </Form>
            {
                groupReportData && Object.keys(groupReportData.pointsOrFunctions).length > 0 ? (
                    Object.keys(groupReportData.pointsOrFunctions).map((key, index) => {
                        return (
                            <Chart
                                style={{
                                    height: `${400 + Math.floor(groupReportData.pointsOrFunctions[key].length / 5) * 15}px`,
                                    maxHeight: '600px'
                                }}
                            >
                                <ChartTitle text={key} />
                                <ChartLegend position="top" orientation="horizontal" />
                                <ChartCategoryAxis>
                                    <ChartCategoryAxisItem
                                        title={{ text: getXAxisTitle() }}
                                        labels={{step: 5}}
                                    />
                                </ChartCategoryAxis>
                                <ChartValueAxis>
                                    <ChartValueAxisItem
                                        title={{ text: getYAxisTitle(key) }}
                                    />
                                </ChartValueAxis>
                                <ChartSeries>
                                    {
                                        groupReportData.pointsOrFunctions[key].map((series, i) => {
                                            return (
                                                <ChartSeriesItem
                                                    key={`${key}-${i}`}
                                                    type="line"
                                                    data={series.data}
                                                    field={"averageValue"}
                                                    categoryField={"key"}
                                                    markers={{visible: false}}
                                                    name={`${series.equipmentId} - ${series.equipmentDisplayName}`}
                                                >
                                                    <ChartSeriesItemTooltip
                                                        visible={true}
                                                        render={tooltipRender}
                                                    />
                                                </ChartSeriesItem>
                                            )
                                        })
                                    }
                                </ChartSeries>
                            </Chart>
                        )
                    })
                ) : null
            }

            <div style={toastSection}>
                <SemanticToastContainer position="bottom-center"/>
            </div>
        </div>
    );
};
